'use client'

import { motion } from 'framer-motion'
import { Globe, Check, Zap, Building2, Store } from 'lucide-react'

interface Track {
  badge: string
  title: string
  tagline: string
  points: string[]
  footnote?: string
}

interface AutomotiveWebsiteSeoSectionProps {
  eyebrow?: string
  heading: string
  intro?: string
  tracks: Track[]
  proof?: string
}

const TRACK_ICONS = [Store, Building2]

export function AutomotiveWebsiteSeoSection({
  eyebrow,
  heading,
  intro,
  tracks,
  proof,
}: AutomotiveWebsiteSeoSectionProps) {
  return (
    <section className="relative py-24 overflow-hidden bg-muted/20">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-14">
          {eyebrow && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="reveal reveal-delay-0 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2F5FE8]/10 border border-[#2F5FE8]/20 mb-6"
            >
              <Globe className="h-4 w-4 text-[#2F5FE8]" />
              <span className="eyebrow">{eyebrow}</span>
            </motion.div>
          )}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="reveal reveal-delay-1 text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight"
          >
            {heading}
          </motion.h2>
          {intro && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="reveal reveal-delay-2 text-lg text-muted-foreground"
            >
              {intro}
            </motion.p>
          )}
        </div>

        {/* Two tracks */}
        <div className="grid gap-6 md:grid-cols-2 max-w-5xl mx-auto">
          {tracks.map((track, index) => {
            const Icon = TRACK_ICONS[index % TRACK_ICONS.length]
            return (
              <motion.div
                key={track.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.12, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="reveal reveal-delay-3 lift-hover flex flex-col rounded-3xl border border-border/50 bg-white dark:bg-zinc-900 p-8 shadow-[0_4px_30px_rgba(0,0,0,0.06)]"
              >
                <div className="flex items-center gap-3 mb-5">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-100 to-blue-200 dark:from-[#2F5FE8]/20 dark:to-[#7EA0F5]/20 border border-blue-200 dark:border-[#2F5FE8]/20">
                    <Icon className="h-5 w-5 text-[#2F5FE8] dark:text-[#7EA0F5]" />
                  </span>
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#2F5FE8]">
                    {track.badge}
                  </span>
                </div>

                <h3 className="reveal reveal-delay-0 text-xl font-bold text-foreground mb-1.5">{track.title}</h3>
                <p className="reveal reveal-delay-1 text-sm font-medium text-[#2F5FE8]/90 mb-6">{track.tagline}</p>

                <ul className="space-y-3 flex-1">
                  {track.points.map((point) => (
                    <li key={point} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 text-[#2F5FE8] flex-shrink-0 mt-0.5" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>

                {track.footnote && (
                  <p className="mt-6 rounded-xl bg-muted/50 p-4 text-sm font-medium text-foreground/80 leading-relaxed">
                    {track.footnote}
                  </p>
                )}
              </motion.div>
            )
          })}
        </div>

        {/* Proof */}
        {proof && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="reveal reveal-delay-1 max-w-4xl mx-auto mt-10 flex items-start gap-3 rounded-2xl border border-[#2F5FE8]/20 bg-[#2F5FE8]/5 p-6"
          >
            <Zap className="h-5 w-5 text-[#2F5FE8] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-foreground leading-relaxed">{proof}</p>
          </motion.div>
        )}
      </div>
    </section>
  )
}
